import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PosterSettings, FrameType, FrameColor } from '@/types/canvas';
import { cn } from '@/lib/utils';
import { Check } from 'lucide-react';
import { getAllVariants } from '@/lib/variants';

interface FrameSelectionProps {
    posterSettings: PosterSettings;
    onPosterSettingsChange: (settings: Partial<PosterSettings>) => void;
}

export function FrameSelection({ posterSettings, onPosterSettingsChange }: FrameSelectionProps) {
    const { t } = useTranslation();
    const [prices, setPrices] = useState({ poster: 39.90, hanger: 14.90, frame: 39.90 });

    useEffect(() => {
        async function loadPrices() {
            try {
                // Use the selected size, default to 'B2' if not set
                const currentSize = posterSettings.size || 'B2';
                const variants = await getAllVariants({ size: currentSize, onlyAvailable: false });

                const posterVariant = variants.find(v => v.frame_type === 'none');
                const frameVariant = variants.find(v => v.frame_type === 'frame' && v.frame_color === 'pine');
                const hangerVariant = variants.find(v => v.frame_type === 'hanger' && v.frame_color === 'pine');

                setPrices({
                    poster: posterVariant?.price_cents ? posterVariant.price_cents / 100 : 39.90,
                    frame: frameVariant?.price_cents ? frameVariant.price_cents / 100 : 39.90,
                    hanger: hangerVariant?.price_cents ? hangerVariant.price_cents / 100 : 14.90,
                });
            } catch (error) {
                console.error('Error loading prices:', error);
            }
        }
        loadPrices();
    }, [posterSettings.size]);

    const handleFrameChange = (frame: FrameType) => {
        // If switching to frame or hanger, ensure a default color is selected if not already
        const newColor = posterSettings.frameColor || 'pine';
        onPosterSettingsChange({ frame, frameColor: newColor });
    };

    const handleColorChange = (e: React.MouseEvent, color: FrameColor, frameType: FrameType) => {
        e.stopPropagation(); // Prevent card click
        // Update both the color AND the frame type (ensure this card becomes selected)
        onPosterSettingsChange({ frameColor: color, frame: frameType });
    };

    return (
        <div className="space-y-4">
            <div className="flex gap-4 overflow-x-auto pb-4 -mx-4 px-4 snap-x">
                {/* Card 1: Poster (No Frame) */}
                <div
                    onClick={() => handleFrameChange('none')}
                    className={cn(
                        "relative flex-none w-[120px] cursor-pointer rounded-xl border-2 transition-all overflow-hidden snap-center group",
                        posterSettings.frame === 'none'
                            ? "border-posterden-red bg-posterden-red/10"
                            : "border-transparent bg-[#252525] hover:border-white/20 shadow-sm"
                    )}
                >
                    <div className="aspect-[3/4] w-full relative bg-gray-100">
                        <img
                            src="/materials/frame-select-poster.png"
                            alt={t('config.poster', { defaultValue: 'Print' })}
                            className="w-full h-full object-cover"
                        />
                    </div>
                    <div className="p-3">
                        <div className="flex justify-between items-center mb-1">
                            <span className="font-medium text-sm text-boho-cream">{t('config.poster', { defaultValue: 'Print' })}</span>
                            {posterSettings.frame === 'none' && (
                                <div className="bg-posterden-red text-white rounded-full p-0.5">
                                    <Check className="w-3 h-3" />
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="text-sm text-boho-cream/90 font-medium">
                        +0.00€
                    </div>
                </div>

                {/* Card 2: Frame - Only show if NOT A2 */}
                {posterSettings.size !== 'A2' && (
                    <div
                        onClick={() => handleFrameChange('frame')}
                        className={cn(
                            "relative flex-none w-[120px] cursor-pointer rounded-xl border-2 transition-all overflow-hidden snap-center group",
                            posterSettings.frame === 'frame'
                                ? "border-posterden-red bg-posterden-red/10"
                                : "border-transparent bg-[#252525] hover:border-white/20 shadow-sm"
                        )}
                    >
                        <div className="aspect-[3/4] w-full relative bg-gray-100">
                            <img
                                src="/materials/frame-select-frame.png"
                                alt={t('config.frame', { defaultValue: 'Frame' })}
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <div className="p-3">
                            <div className="flex justify-between items-center mb-1">
                                <span className="font-medium text-sm text-boho-cream">{t('config.frame', { defaultValue: 'Frame' })}</span>
                                {posterSettings.frame === 'frame' && (
                                    <div className="bg-posterden-red text-white rounded-full p-0.5">
                                        <Check className="w-3 h-3" />
                                    </div>
                                )}
                            </div>
                            <div className="text-sm text-boho-cream/90 font-medium mb-3">
                                +{prices.frame.toFixed(2)}€
                            </div>

                            {/* Embedded Variants */}
                            <div className="flex gap-2 justify-center">
                                {[
                                    { value: 'oak', color: '#B9AB93', label: 'Oak' },
                                    { value: 'white', color: '#F5F5F0', label: 'White' },
                                    { value: 'black', color: '#2A2A2A', label: 'Black' },
                                ].map((fc) => (
                                    <div
                                        key={fc.value}
                                        onClick={(e) => handleColorChange(e, fc.value as FrameColor, 'frame')}
                                        className={cn(
                                            "w-6 h-6 rounded-full border border-gray-200 shadow-sm cursor-pointer hover:scale-110 transition-transform",
                                            posterSettings.frame === 'frame' && posterSettings.frameColor === fc.value
                                                ? "ring-2 ring-offset-1 ring-offset-[#252525] ring-posterden-red"
                                                : ""
                                        )}
                                        style={{ backgroundColor: fc.color }}
                                        title={t(`colors.${fc.value}`, { defaultValue: fc.label })}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Card 3: Hanger */}
                <div
                    onClick={() => handleFrameChange('hanger')}
                    className={cn(
                        "relative flex-none w-[120px] cursor-pointer rounded-xl border-2 transition-all overflow-hidden snap-center group",
                        posterSettings.frame === 'hanger'
                            ? "border-posterden-red bg-posterden-red/10"
                            : "border-transparent bg-[#252525] hover:border-white/20 shadow-sm"
                    )}
                >
                    <div className="aspect-[3/4] w-full relative bg-gray-100">
                        <img
                            src={posterSettings.size === 'A2' ? "/materials/frame-select-hanger.png" : "/materials/frame-select-hanger-b2.png"}
                            alt={t('config.hanger', { defaultValue: 'Hanger' })}
                            className="w-full h-full object-cover"
                        />
                    </div>
                    <div className="p-3">
                        <div className="flex justify-between items-center mb-1">
                            <span className="font-medium text-sm text-boho-cream">{t('config.hanger', { defaultValue: 'Hanger' })}</span>
                            {posterSettings.frame === 'hanger' && (
                                <div className="bg-posterden-red text-white rounded-full p-0.5">
                                    <Check className="w-3 h-3" />
                                </div>
                            )}
                        </div>
                        <div className="text-sm text-boho-cream/90 font-medium mb-3">
                            +{prices.hanger.toFixed(2)}€
                        </div>

                        {/* Embedded Variants */}
                        <div className="flex gap-2 justify-center">
                            {[
                                { value: 'pine', color: '#E6DBC6', label: 'Pine' },
                                { value: 'teak', color: '#B38B6D', label: 'Teak' },
                                { value: 'walnut', color: '#4E3B31', label: 'Dark Walnut' },
                            ].map((fc) => (
                                <div
                                    key={fc.value}
                                    onClick={(e) => handleColorChange(e, fc.value as FrameColor, 'hanger')}
                                    className={cn(
                                        "w-6 h-6 rounded-full border border-gray-200 shadow-sm cursor-pointer hover:scale-110 transition-transform",
                                        posterSettings.frame === 'hanger' && posterSettings.frameColor === fc.value
                                            ? "ring-2 ring-offset-1 ring-offset-[#252525] ring-posterden-red"
                                            : ""
                                    )}
                                    style={{ backgroundColor: fc.color }}
                                    title={t(`colors.${fc.value}`, { defaultValue: fc.label })}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div >
    );
}
